import { roles } from 'common-src/models/Client';
import { TaskReason } from 'common-src/models/PatientTask/constants';

import { CLIENT_ROOT } from 'src/constants/paths';

export const EVIDENCE_TYPES = {
  'note-gap': { label: 'Missing from visit note', icon: '⊘' },
  'protocol-gap': { label: 'Protocol step missing', icon: '△' },
  'time-expiring': { label: 'Deadline approaching', icon: '◷' },
  'state-mismatch': { label: 'Needs update', icon: '≠' },
  'cadence-drift': { label: 'Overdue', icon: '↝' },
  'gate-blocked': { label: 'Blocker', icon: '⊗' },
  'safety-trigger': { label: 'Safety check', icon: '!' },
  'transition-trigger': { label: 'Transition step', icon: '⇥' },
};

export const ASSESSMENT_TASK_TYPES = {
  MoCA: { value: 'moca', label: 'MoCA' },
  MIS: { value: 'mis', label: 'MIS' },
  FullMoCA: { value: 'moca_visual', label: 'MoCA Visual' },
  MoCAVisualAthena: { value: 'moca_visual_athena', label: 'MoCA Visual Athena' },
  PRO10: { value: 'pro10', label: 'PRO-10' },
  ZBI22: { value: 'zbi22', label: 'ZBI-22' },
};

export const ASSESSMENT_TASK_VARIANTS = {
  DIGITAL_AVATAR: { value: 'Digital Avatar', label: 'Digital Avatar' },
  DIGITAL: { value: 'Digital', label: 'Digital' },
  STANDARD: { value: 'Standard', label: 'Standard' },
};

export const VISIT_TYPES = {
  99205: {
    label: 'New Patient Visit (99205)',
    short: '99205',
    pathway: 'cognitive',
    phase: 1,
    readinessDimensions: ['scheduling', 'stakeholders'],
    description: 'First visit - intake, history, and initial plan',
  },
  99483: {
    label: 'Cognitive Assessment (99483)',
    short: '99483',
    pathway: 'cognitive',
    phase: 2,
    readinessDimensions: ['evidence', 'assessments', 'scheduling', 'stakeholders'],
    description: 'Full cognitive workup - requires labs, MRI, Cryos, and caregiver present',
  },
};

export const READINESS_BADGE_META_BY_STATUS = {
  green: { label: 'READY', variant: 'green' },
  orange: { label: 'CANCELED', variant: 'orange' },
  yellow: { label: 'AT RISK', variant: 'yellow' },
  red: { label: 'NOT READY', variant: 'red' },
};

export const POST_VISIT_STALL_DAYS = 10;

export const POST_VISIT_BADGE_META_BY_STATUS = {
  green: { label: 'FOLLOW-UP COMPLETE', variant: 'green' },
  yellow: { label: 'FOLLOW-UP IN PROGRESS', variant: 'yellow' },
  red: { label: 'FOLLOW-UP STALLED', variant: 'red' },
};

export const TASK_PRIORITY = {
  Critical: 'critical',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

export const PRIORITY_SORT_ORDER = {
  [TASK_PRIORITY.Critical]: 0,
  [TASK_PRIORITY.High]: 1,
  [TASK_PRIORITY.Medium]: 2,
  [TASK_PRIORITY.Low]: 3,
};

export const OrgScope = {
  Mindspan: 'mindspan',
  Perry: 'perry',
};

export const ACTION_AREA_PAGE = {
  Huddle: 'huddle',
  PostVisit: 'post-visit',
  VisitsDue: 'visits-due',
};

export const TASK_QUEUE_PAGE_SIZE = 200;

export const MANUAL_TASK_CANCEL_FEATURE_FLAG = 'manualTaskCancel';

export const TASK_QUEUE_SORT_MODE = {
  PatientPriority: 1,
  ReasonPriority: 2,
  Priority: 3,
};

export const TASK_QUEUE_SORT_MODE_OPTIONS = [
  {
    value: TASK_QUEUE_SORT_MODE.PatientPriority,
    label: 'Member + Priority',
  },
  {
    value: TASK_QUEUE_SORT_MODE.ReasonPriority,
    label: 'Reason + Priority',
  },
  {
    value: TASK_QUEUE_SORT_MODE.Priority,
    label: 'Priority',
  },
];

const formatTaskQueueReasonLabel = (value = '') =>
  `${value}`
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();

const TASK_QUEUE_REASON_LABEL_OVERRIDES = {
  CognitiveResultsNormal: 'Screenings Normal',
  CognitiveResultsAbnormal: 'Screenings Abnormal',
  CognitiveResultsCritical: 'Screenings Critical',
};

export const TASK_QUEUE_REASON = Object.freeze(
  Object.entries(TaskReason).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value.value,
    }),
    {},
  ),
);

export const TASK_QUEUE_REASON_LABELS = Object.freeze(
  Object.entries(TaskReason).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [value.value]: TASK_QUEUE_REASON_LABEL_OVERRIDES[key] ?? formatTaskQueueReasonLabel(key),
    }),
    {},
  ),
);

export const TASK_QUEUE_PRIORITY_GROUPS = Object.freeze([
  { key: TASK_PRIORITY.Critical, label: 'Critical', badgeVariant: 'critical' },
  { key: TASK_PRIORITY.High, label: 'High', badgeVariant: 'outline' },
  { key: TASK_PRIORITY.Medium, label: 'Medium', badgeVariant: 'outline' },
  { key: TASK_PRIORITY.Low, label: 'Low', badgeVariant: 'outline' },
]);

export const TASK_QUEUE_SEARCH_PLACEHOLDERS = Object.freeze({
  [TASK_QUEUE_SORT_MODE.PatientPriority]: 'Search by member...',
  [TASK_QUEUE_SORT_MODE.ReasonPriority]: 'Search by reason...',
  [TASK_QUEUE_SORT_MODE.Priority]: 'Search by priority...',
});

export const TASK_QUEUE_OWNER_TABS = Object.freeze([
  { key: 'all', label: 'All' },
  { key: 'my', label: 'My Tasks' },
  { key: 'following', label: 'Following' },
  { key: 'unassigned', label: 'Unassigned' },
]);

export const TASK_QUEUE_TEAM_TABS = Object.freeze([
  { key: 'all', label: 'All' },
  { key: 'member', label: 'Member' },
  { key: 'ops', label: 'Ops' },
  { key: 'clinical', label: 'Clinical' },
  { key: 'rcm', label: 'RCM' },
]);

export const TASK_QUEUE_STATUS_TABS = Object.freeze([
  { key: 'open', label: 'Open' },
  { key: 'critical', label: 'Critical' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'escalated', label: 'Escalated' },
  { key: 'messages', label: 'Messages' },
  { key: 'assessments', label: 'Assessments' },
]);

export const PRIORITY_BADGE_COPY = {
  [TASK_PRIORITY.Critical]: 'CRITICAL',
  [TASK_PRIORITY.High]: 'HIGH',
  [TASK_PRIORITY.Medium]: 'MEDIUM',
  [TASK_PRIORITY.Low]: 'LOW',
};

export const PRIORITY_OPTIONS = Object.freeze([
  TASK_PRIORITY.Critical,
  TASK_PRIORITY.High,
  TASK_PRIORITY.Medium,
  TASK_PRIORITY.Low,
]);

export const TASK_ASSIGNEE_PICKER_MODE = Object.freeze({
  Team: 'team',
  User: 'user',
});

export const TASK_STATUS = {
  Scheduled: 0,
  New: 1,
  InProgress: 2,
  Completed: 3,
  Canceled: 4,
  UnableToComplete: 5,
};

export const TASK_DISPLAY_STATUS = {
  Pending: 'pending',
  InProgress: 'in-progress',
  Done: 'done',
};

export const TASK_DISPLAY_STATUS_BY_STATUS = {
  [TASK_STATUS.Scheduled]: TASK_DISPLAY_STATUS.Pending,
  [TASK_STATUS.New]: TASK_DISPLAY_STATUS.Pending,
  [TASK_STATUS.InProgress]: TASK_DISPLAY_STATUS.InProgress,
  [TASK_STATUS.Completed]: TASK_DISPLAY_STATUS.Done,
  [TASK_STATUS.Canceled]: TASK_DISPLAY_STATUS.Done,
  [TASK_STATUS.UnableToComplete]: TASK_DISPLAY_STATUS.Done,
};

export const TASK_CALLBACK_STATUS = {
  Pending: 'pending',
  Success: 'success',
  Error: 'error',
};

export const TASK_STEP_TYPE = {
  Manual: 'manual',
  Automatic: 'automatic',
};

export const BLOCKED_REASON = {
  AwaitingResults: 0,
  PatientNoShow: 1,
  PriorAuthPending: 2,
  CaregiverUnresponsive: 3,
  WaitingOnReferral: 4,
  Other: 5,
};

export const BLOCKED_STATUS = {
  Blocked: 0,
  Unblocked: 1,
};

export const ESCALATED_STATUS = {
  Escalated: 0,
  Deescalated: 1,
};

export const ESCALATED_TO = {
  MemberExperience: 0,
  Operations: 1,
  Clinical: 2,
  RCM: 3,
};

export const BLOCKED_REASON_OPTIONS = [
  { value: BLOCKED_REASON.AwaitingResults, label: 'Awaiting results' },
  { value: BLOCKED_REASON.PatientNoShow, label: 'Patient no-show' },
  { value: BLOCKED_REASON.PriorAuthPending, label: 'Prior auth pending' },
  { value: BLOCKED_REASON.CaregiverUnresponsive, label: 'Caregiver unresponsive' },
  { value: BLOCKED_REASON.WaitingOnReferral, label: 'Waiting on referral' },
  { value: BLOCKED_REASON.Other, label: 'Other' },
];

export const BLOCKED_REASON_LABELS = BLOCKED_REASON_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {},
);

export const ESCALATED_TO_OPTIONS = [
  { value: ESCALATED_TO.MemberExperience, label: 'Member Experience', icon: '○' },
  { value: ESCALATED_TO.Clinical, label: 'Clinical', icon: '●' },
  { value: ESCALATED_TO.Operations, label: 'Operations', icon: '◎' },
  { value: ESCALATED_TO.RCM, label: 'RCM', icon: '◇' },
];

export const ROLE_ID_TO_ESCALATION_TEAM = {
  [roles.MXSpecialist.id]: ESCALATED_TO.MemberExperience,
  [roles.AccountabilityCoach.id]: [ESCALATED_TO.MemberExperience, ESCALATED_TO.Clinical],

  [roles.Enrollment.id]: ESCALATED_TO.Operations,
  [roles.Admin.id]: ESCALATED_TO.Operations,

  [roles.RN.id]: ESCALATED_TO.Clinical,
  [roles.CDE.id]: ESCALATED_TO.Clinical,
  [roles.Physician.id]: ESCALATED_TO.Clinical,
  [roles.ExternalPhysician.id]: ESCALATED_TO.Clinical,
  [roles.RD.id]: ESCALATED_TO.Clinical,
  [roles.NP.id]: ESCALATED_TO.Clinical,
  [roles.SocialWorker.id]: ESCALATED_TO.Clinical,
  [roles.Neurologist.id]: ESCALATED_TO.Clinical,
  [roles.CareNavigator.id]: ESCALATED_TO.Clinical,
  [roles.NPNeurologist.id]: ESCALATED_TO.Clinical,

  [roles.RCM.id]: ESCALATED_TO.RCM,
};

export const DEFAULT_STATS = {
  total: 0,
  member: 0,
  ops: 0,
  clinical: 0,
  rcm: 0,
  cognitive: 0,
  kisunla: 0,
  'leqembi-iv': 0,
  'leqembi-sc': 0,
  critical: 0,
  blocked: 0,
  escalated: 0,
  messages: 0,
  assessments: 0,
};

export const DEFAULT_OWNER_STATS = {
  all: 0,
  my: 0,
  following: 0,
  unassigned: 0,
};

export const TASK_COMPLETE_REFRESH_INTERVAL_MS = 1000;
export const TASK_COMPLETE_REFRESH_ATTEMPTS = 8;
export const TASK_COMPLETE_STABLE_REFRESHES = 2;

export const TAB_KEYS = {
  READINESS: 'readiness',
  TASKS: 'tasks',
  LEADS: 'leads',
  BULK_ASSIGN: 'bulk-assign',
  ACTION_AREA: 'action-area',
  PROVIDER_HUD: 'provider-hud',
  MEMBER_RECORD: 'member',
};

export const WORKFLOW_SCOPE = {
  PRE_VISIT: 'pre_visit',
  POST_VISIT: 'post_visit',
  SPLIT: 'split',
};

export const CARE_ORCHESTRATION_BASE_PATH = `${CLIENT_ROOT}/care-orchestration`;

export const TASK_STATUS_FILTER_KEYS = [
  'open',
  'critical',
  'blocked',
  'escalated',
  'messages',
  'assessments',
];

export const getGateBadgeLabel = (days) => {
  if (!Number.isFinite(days)) return null;
  if (days <= 2) return 'T-2 (final check)';
  if (days <= 7) return 'T-7 (action needed)';
  return 'T-14+ (on track)';
};

export const PATHWAY = {
  COGNITIVE: 'cognitive',
  DIABETES: 'diabetes',
  KISUNLA: 'kisunla',
  LEQEMBI_IV: 'leqembi-iv',
  LEQEMBI_SC: 'leqembi-sc',
};

export const pathwayLabel = {
  [PATHWAY.COGNITIVE]: 'Cognitive',
  [PATHWAY.DIABETES]: 'Diabetes',
  [PATHWAY.KISUNLA]: 'Kisunla',
  [PATHWAY.LEQEMBI_IV]: 'Leqembi IV',
  [PATHWAY.LEQEMBI_SC]: 'Leqembi SC',
};

export const teamMeta = {
  member: { label: 'Member Experience', icon: '○' },
  ops: { label: 'Operations', icon: '◎' },
  clinical: { label: 'Clinical', icon: '●' },
  rcm: { label: 'RCM', icon: '◌' },
};

export const statusColor = {
  green: '#22C55E',
  orange: '#EA580C',
  yellow: '#EAB308',
  red: '#EF4444',
};
export const statusBg = { green: '#F0FDF4', yellow: '#FEFCE8', red: '#FEF2F2' };
export const statusBorder = { green: '#BBF7D0', yellow: '#FDE68A', red: '#FECACA' };

export const NOTIFICATION_CHANNELS = [
  { key: 'browser', label: 'Browser' },
  { key: 'inApp', label: 'In-app' },
  { key: 'slack', label: 'Slack' },
  { key: 'email', label: 'Email' },
];

export const NOTIFICATION_CATEGORIES = [
  { key: 'critical', label: 'Critical' },
  { key: 'assignment', label: 'Assignments' },
  { key: 'escalation', label: 'Escalations' },
  { key: 'followedChange', label: 'Followed changes' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'subtasksCompleted', label: 'Sub-tasks completed' },
  { key: 'subtaskCanceled', label: 'Sub-task canceled' },
  { key: 'leadUpdates', label: 'Lead updates (consent accepted, patient created)' },
];

export const DEFAULT_NOTIFICATION_PREFERENCES = {
  notificationsEnabled: false,
  channels: {
    browser: {
      enabled: false,
    },
    inApp: {
      enabled: false,
    },
    slack: {
      enabled: false,
      slackUserId: '',
    },
    email: {
      enabled: false,
      emailAddress: '',
    },
  },
  categories: {
    critical: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
    assignment: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
    escalation: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
    followedChange: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
    overdue: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
    subtasksCompleted: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
    subtaskCanceled: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
    leadUpdates: {
      browser: false,
      inApp: false,
      slack: false,
      email: false,
    },
  },
  sound: true,
};

export const NOTIFICATION_CATEGORY = {
  Critical: 1,
  Assignments: 2,
  Escalations: 3,
  FollowedChanges: 4,
  Overdue: 5,
  SubtasksCompleted: 6,
  SubtaskCanceled: 7,
  // Lead intake: sent to whoever claimed the lead's outreach task.
  LeadConsentAccepted: 8,
  LeadPatientCreated: 9,
};

export const NOTIFICATION_STATUS = {
  Sent: 1,
  Read: 2,
  Dismissed: 3,
  Pending: 4,
};

export const NOTIFICATION_CATEGORY_KEY_BY_ID = {
  [NOTIFICATION_CATEGORY.Critical]: 'critical',
  [NOTIFICATION_CATEGORY.Assignments]: 'assignment',
  [NOTIFICATION_CATEGORY.Escalations]: 'escalation',
  [NOTIFICATION_CATEGORY.FollowedChanges]: 'followedChange',
  [NOTIFICATION_CATEGORY.Overdue]: 'overdue',
  [NOTIFICATION_CATEGORY.SubtasksCompleted]: 'subtasksCompleted',
  [NOTIFICATION_CATEGORY.SubtaskCanceled]: 'subtaskCanceled',
  [NOTIFICATION_CATEGORY.LeadConsentAccepted]: 'leadUpdates',
  [NOTIFICATION_CATEGORY.LeadPatientCreated]: 'leadUpdates',
};

export const NOTIFICATION_CATEGORY_LABEL_BY_ID = {
  [NOTIFICATION_CATEGORY.Critical]: 'Critical',
  [NOTIFICATION_CATEGORY.Assignments]: 'Assignments',
  [NOTIFICATION_CATEGORY.Escalations]: 'Escalations',
  [NOTIFICATION_CATEGORY.FollowedChanges]: 'Followed changes',
  [NOTIFICATION_CATEGORY.Overdue]: 'Overdue',
  [NOTIFICATION_CATEGORY.SubtasksCompleted]: 'Sub-tasks completed',
  [NOTIFICATION_CATEGORY.SubtaskCanceled]: 'Sub-task canceled',
  [NOTIFICATION_CATEGORY.LeadConsentAccepted]: 'Consent accepted',
  [NOTIFICATION_CATEGORY.LeadPatientCreated]: 'Patient created',
};

export const NOTIFICATION_COLOR = {
  [NOTIFICATION_CATEGORY.Critical]: '#ef4444',
  [NOTIFICATION_CATEGORY.Assignments]: '#3b82f6',
  [NOTIFICATION_CATEGORY.Escalations]: '#a855f7',
  [NOTIFICATION_CATEGORY.FollowedChanges]: '#22c55e',
  [NOTIFICATION_CATEGORY.Overdue]: '#eab308',
  [NOTIFICATION_CATEGORY.SubtasksCompleted]: '#22C55E',
  [NOTIFICATION_CATEGORY.SubtaskCanceled]: '#64748B',
  [NOTIFICATION_CATEGORY.LeadConsentAccepted]: '#0EA5E9',
  [NOTIFICATION_CATEGORY.LeadPatientCreated]: '#22C55E',
};

export const TASK_NOTIFICATION_TOAST_VARIANT = {
  Critical: 'critical',
  Assignments: 'assignment',
  Escalations: 'escalation',
  FollowedChanges: 'followedChange',
  Overdue: 'overdue',
  SubtasksCompleted: 'subtasksCompleted',
  SubtaskCanceled: 'subtaskCanceled',
};

export const NOTIFICATION_TOAST_VARIANT_BY_CATEGORY = {
  [NOTIFICATION_CATEGORY.Critical]: TASK_NOTIFICATION_TOAST_VARIANT.Critical,
  [NOTIFICATION_CATEGORY.Assignments]: TASK_NOTIFICATION_TOAST_VARIANT.Assignments,
  [NOTIFICATION_CATEGORY.Escalations]: TASK_NOTIFICATION_TOAST_VARIANT.Escalations,
  [NOTIFICATION_CATEGORY.FollowedChanges]: TASK_NOTIFICATION_TOAST_VARIANT.FollowedChanges,
  [NOTIFICATION_CATEGORY.Overdue]: TASK_NOTIFICATION_TOAST_VARIANT.Overdue,
  [NOTIFICATION_CATEGORY.SubtasksCompleted]: TASK_NOTIFICATION_TOAST_VARIANT.SubtasksCompleted,
  [NOTIFICATION_CATEGORY.SubtaskCanceled]: TASK_NOTIFICATION_TOAST_VARIANT.SubtaskCanceled,
  // Same green success styling as completed sub-tasks.
  [NOTIFICATION_CATEGORY.LeadConsentAccepted]: TASK_NOTIFICATION_TOAST_VARIANT.SubtasksCompleted,
  [NOTIFICATION_CATEGORY.LeadPatientCreated]: TASK_NOTIFICATION_TOAST_VARIANT.SubtasksCompleted,
};

export const NOTIFICATION_ICON_KEY = {
  [NOTIFICATION_CATEGORY.Critical]: 'critical',
  [NOTIFICATION_CATEGORY.Assignments]: 'assignments',
  [NOTIFICATION_CATEGORY.Escalations]: 'escalations',
  [NOTIFICATION_CATEGORY.FollowedChanges]: 'followedChanges',
  [NOTIFICATION_CATEGORY.Overdue]: 'overdue',
  [NOTIFICATION_CATEGORY.SubtasksCompleted]: 'subtasksCompleted',
  [NOTIFICATION_CATEGORY.SubtaskCanceled]: 'subtaskCanceled',
  [NOTIFICATION_CATEGORY.LeadConsentAccepted]: 'leadUpdates',
  [NOTIFICATION_CATEGORY.LeadPatientCreated]: 'leadUpdates',
};

export const NOTIFICATION_BROWSER_COPY_BY_CATEGORY = {
  [NOTIFICATION_CATEGORY.Critical]: {
    title: 'Critical Task Requires Attention',
    body: 'A task has been flagged as critical priority',
    requireInteraction: true,
  },
  [NOTIFICATION_CATEGORY.Assignments]: {
    title: 'New Task Assignment',
    body: 'A task has been assigned to you',
    requireInteraction: false,
  },
  [NOTIFICATION_CATEGORY.Escalations]: {
    title: 'Task Escalated to You',
    body: 'A task has been escalated to your attention',
    requireInteraction: false,
  },
  [NOTIFICATION_CATEGORY.FollowedChanges]: {
    title: 'Followed Task Updated',
    body: "A task you're following has changed status",
    requireInteraction: false,
  },
  [NOTIFICATION_CATEGORY.Overdue]: {
    title: 'Task Overdue',
    body: 'A task you own or follow is past its due date',
    requireInteraction: false,
  },
  [NOTIFICATION_CATEGORY.SubtasksCompleted]: {
    title: 'Sub-tasks Completed',
    body: 'All sub-tasks of a task you follow or own have been completed - parent task ready for processing',
    requireInteraction: false,
  },
  [NOTIFICATION_CATEGORY.SubtaskCanceled]: {
    title: 'Sub-task Canceled',
    body: 'A sub-task of a task you follow or own has been canceled',
    requireInteraction: false,
  },
  [NOTIFICATION_CATEGORY.LeadConsentAccepted]: {
    title: 'Consent Accepted',
    body: 'A lead you worked accepted the consent form',
    requireInteraction: false,
  },
  [NOTIFICATION_CATEGORY.LeadPatientCreated]: {
    title: 'Patient Created',
    body: 'A lead you worked is now a patient',
    requireInteraction: false,
  },
};

export const TASK_NOTIFICATION_TOAST_DISMISS_MS = 4000;
export const TASK_NOTIFICATION_TOAST_STAGGER_MS = 1500;
export const MAX_TASK_NOTIFICATION_TOASTS = 3;
export const TASK_NOTIFICATION_TOASTED_IDS_STORAGE_KEY =
  'careOrchestrationTaskNotificationToastedIds';
export const TASK_NOTIFICATION_TOASTED_IDS_MAX = 200;
export const TASK_NOTIFICATION_BROWSER_PERMISSION_HINT =
  'Enable browser notifications to get notifications when Orchestration Manager is in the background.';
